import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../../components/ui/card';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Badge } from '../../components/ui/badge';
import { 
  QrCode, Plus, Printer, Download, History, 
  Smartphone, FileText, Package 
} from 'lucide-react';
import api, { qrAPI, jobsAPI } from '../../utils/api';

const QRCodeManagement = () => {
  const { t } = useTranslation();
  const [batches, setBatches] = useState([]);
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showGenerateDialog, setShowGenerateDialog] = useState(false);
  const [generatedBatch, setGeneratedBatch] = useState(null);
  
  const [generateForm, setGenerateForm] = useState({
    quantity: 10,
    job_id: '',
    printer_type: 'standard'
  });

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [batchesRes, jobsRes] = await Promise.all([
        api.get('/qr/batches'),
        api.get('/jobs')
      ]);
      setBatches(batchesRes.data);
      setJobs(jobsRes.data);
    } catch (error) {
      toast.error('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const handleGenerateBatch = async () => {
    if (generateForm.quantity < 1 || generateForm.quantity > 1000) {
      toast.error('Quantity must be between 1 and 1000');
      return;
    }

    try {
      const response = await api.post('/qr/batch', {
        quantity: generateForm.quantity,
        job_id: generateForm.job_id || null,
        printer_type: generateForm.printer_type
      });
      
      setGeneratedBatch(response.data);
      toast.success(`Generated ${response.data.quantity} QR codes`);
      fetchData();
    } catch (error) {
      toast.error('Failed to generate QR codes');
    }
  };

  const printQRCodes = (codes, printerType) => {
    // Open print dialog with formatted QR codes
    const printWindow = window.open('', '_blank');
    
    const labelSize = printerType === 'thermal' 
      ? 'width: 2in; height: 1in;' 
      : printerType === 'mobile'
      ? 'width: 1.5in; height: 1in;'
      : 'width: 2in; height: 2in;';
    
    printWindow.document.write(`
      <html>
        <head>
          <title>MoveMate QR Codes</title>
          <script src="https://cdn.jsdelivr.net/npm/qrcode@1.5.3/build/qrcode.min.js"></script>
          <style>
            @page { margin: 0.25in; }
            body { font-family: Arial, sans-serif; }
            .qr-grid { 
              display: grid; 
              grid-template-columns: repeat(auto-fill, minmax(2in, 1fr));
              gap: 0.5in;
            }
            .qr-label {
              ${labelSize}
              border: 1px dashed #ccc;
              padding: 0.25in;
              text-align: center;
              page-break-inside: avoid;
            }
            .qr-code { margin-bottom: 0.1in; }
            .qr-text { font-size: 10px; font-weight: bold; }
            .company-name { font-size: 8px; color: #666; }
          </style>
        </head>
        <body>
          <div class="qr-grid">
            ${codes.map(code => `
              <div class="qr-label">
                <div class="qr-code" id="qr-${code.code}"></div>
                <div class="qr-text">${code.code}</div>
                <div class="company-name">${code.company_name}</div>
              </div>
            `).join('')}
          </div>
          <script>
            ${codes.map(code => `
              QRCode.toCanvas(document.querySelector('#qr-${code.code}'), '${window.location.origin}/scan/${code.code}', {
                width: 80,
                margin: 1
              });
            `).join('\n')}
            setTimeout(() => window.print(), 500);
          </script>
        </body>
      </html>
    `);
    printWindow.document.close();
  };

  const downloadCSV = (codes) => {
    const csv = [
      'Code,URL,Company',
      ...codes.map(c => `${c.code},${window.location.origin}/scan/${c.code},${c.company_name}`)
    ].join('\n');
    
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `qr-codes-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="space-y-6" data-testid="qr-code-management">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-bold mb-2">QR Code Management</h1>
          <p className="text-gray-600">Generate and print QR codes for inventory tracking</p>
        </div>
        <Dialog open={showGenerateDialog} onOpenChange={(open) => {
          setShowGenerateDialog(open);
          if (!open) setGeneratedBatch(null);
        }}>
          <DialogTrigger asChild>
            <Button data-testid="generate-qr-btn">
              <Plus size={20} className="mr-2" />
              Generate QR Codes
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-2xl">
            <DialogHeader>
              <DialogTitle>Generate QR Codes</DialogTitle>
              <DialogDescription>
                Create a batch of QR codes for inventory items
              </DialogDescription>
            </DialogHeader>

            {generatedBatch ? (
              <div className="space-y-4">
                <div className="bg-green-50 border border-green-200 rounded-lg p-4 text-center">
                  <QrCode size={48} className="mx-auto text-green-600 mb-2" />
                  <h3 className="text-xl font-bold text-green-900">
                    {generatedBatch.quantity} QR Codes Generated!
                  </h3>
                  <p className="text-sm text-green-700">Batch ID: {generatedBatch.batch_id}</p>
                </div>

                <div className="grid grid-cols-2 gap-3">
                  <Button 
                    onClick={() => printQRCodes(generatedBatch.codes, generatedBatch.printer_type)}
                    className="h-20 flex-col"
                  >
                    <Printer size={24} className="mb-2" />
                    Print Labels
                  </Button>
                  <Button 
                    variant="outline"
                    onClick={() => downloadCSV(generatedBatch.codes)}
                    className="h-20 flex-col"
                  >
                    <Download size={24} className="mb-2" />
                    Download CSV
                  </Button>
                </div>

                <div className="border rounded-lg p-4 max-h-48 overflow-y-auto">
                  <p className="text-sm font-semibold mb-2">Generated Codes:</p>
                  <div className="grid grid-cols-4 gap-2 text-xs">
                    {generatedBatch.codes.slice(0, 20).map(code => (
                      <code key={code.code} className="bg-gray-100 px-2 py-1 rounded">
                        {code.code}
                      </code>
                    ))}
                    {generatedBatch.codes.length > 20 && (
                      <span className="text-gray-500">+{generatedBatch.codes.length - 20} more</span>
                    )}
                  </div>
                </div>

                <Button 
                  variant="outline" 
                  className="w-full"
                  onClick={() => {
                    setGeneratedBatch(null);
                    setShowGenerateDialog(false);
                  }}
                >
                  Done
                </Button>
              </div>
            ) : (
              <div className="space-y-4">
                <div>
                  <Label>Quantity *</Label>
                  <Input
                    type="number"
                    min="1"
                    max="1000"
                    value={generateForm.quantity}
                    onChange={(e) => setGenerateForm({ ...generateForm, quantity: parseInt(e.target.value) || 1 })}
                    data-testid="qr-quantity-input"
                  />
                  <p className="text-xs text-gray-500 mt-1">Max 1000 per batch</p>
                </div>

                <div>
                  <Label>Link to Job (Optional)</Label>
                  <Select 
                    value={generateForm.job_id || "none"} 
                    onValueChange={(value) => setGenerateForm({ ...generateForm, job_id: value === "none" ? "" : value })}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select a job..." />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">No job (general use)</SelectItem>
                      {jobs.map(job => (
                        <SelectItem key={job.id} value={job.id}>
                          {job.job_number} - {job.from_address?.city} → {job.to_address?.city}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                <div>
                  <Label>Printer Type</Label>
                  <Select 
                    value={generateForm.printer_type} 
                    onValueChange={(value) => setGenerateForm({ ...generateForm, printer_type: value })}
                  >
                    <SelectTrigger>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="standard">
                        <div className="flex items-center gap-2">
                          <FileText size={16} />
                          Standard (A4/Letter)
                        </div>
                      </SelectItem>
                      <SelectItem value="thermal">
                        <div className="flex items-center gap-2">
                          <Printer size={16} />
                          Thermal (2x1 inch labels)
                        </div>
                      </SelectItem>
                      <SelectItem value="mobile">
                        <div className="flex items-center gap-2">
                          <Smartphone size={16} />
                          Mobile (Bluetooth printer)
                        </div>
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                <Button 
                  onClick={handleGenerateBatch} 
                  className="w-full"
                  data-testid="confirm-generate-qr"
                >
                  <QrCode size={18} className="mr-2" />
                  Generate {generateForm.quantity} QR Codes
                </Button>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>

      {/* Stats */}
      <div className="grid md:grid-cols-3 gap-4">
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Batches</p>
                <p className="text-3xl font-bold">{batches.length}</p>
              </div>
              <History className="text-primary" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Total Codes</p>
                <p className="text-3xl font-bold">
                  {batches.reduce((sum, b) => sum + b.count, 0)}
                </p>
              </div>
              <QrCode className="text-blue-600" size={32} />
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-gray-600">Linked to Jobs</p>
                <p className="text-3xl font-bold">
                  {batches.filter(b => b.job_id).length}
                </p>
              </div>
              <Package className="text-green-600" size={32} />
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Batch History */}
      <Card>
        <CardHeader>
          <CardTitle>Batch History</CardTitle>
          <CardDescription>Previously generated QR code batches</CardDescription>
        </CardHeader>
        <CardContent>
          {loading ? (
            <div className="text-center py-8">Loading...</div>
          ) : batches.length === 0 ? (
            <div className="text-center py-12">
              <QrCode className="mx-auto text-gray-400 mb-4" size={48} />
              <p className="text-gray-600">No QR codes generated yet</p>
              <Button 
                variant="outline" 
                className="mt-4"
                onClick={() => setShowGenerateDialog(true)}
              >
                Generate First Batch
              </Button>
            </div>
          ) : (
            <div className="space-y-3">
              {batches.map((batch) => (
                <div 
                  key={batch._id}
                  className="p-4 border rounded-lg hover:shadow-md transition-shadow"
                  data-testid={`batch-${batch._id}`}
                >
                  <div className="flex justify-between items-center">
                    <div>
                      <div className="flex items-center gap-2">
                        <h3 className="font-semibold">Batch {batch._id}</h3>
                        <Badge variant="outline">{batch.count} codes</Badge>
                      </div>
                      <p className="text-sm text-gray-500 mt-1">
                        Created: {new Date(batch.created_at).toLocaleDateString()}
                      </p>
                      {batch.job_id && (
                        <p className="text-xs text-blue-600 mt-1">
                          Linked to job
                        </p>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Button variant="outline" size="sm">
                        <Printer size={16} className="mr-1" />
                        Reprint
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default QRCodeManagement;
